<template>
	<div
		class="product-overlay"
		v-if="product"
	>
		<div class="product-overlay__top">
			<div class="product-overlay__info">
				<ProductRating
					class="product-overlay__rating"
					:rating="product.averageRatings"
				/>
				<h2
					class="product-overlay__name"
					v-html="product.name"
				/>
				<div
					class="product-overlay__description"
					v-html="product.description"
				/>

				<div
					class="products__item-variants"
					v-if="availableVariants.length > 1"
				>
					<select
						class="products__item-variants-select"
						v-model="selectedVariant"
					>
						<option
							v-for="(variant, index) in availableVariants"
							:key="variant.code"
							:value="index"
							v-html="variant.name"
						/>
					</select>
				</div>

				<div
					v-if="price || !product.isConfigurable || priceReduction"
					class="products__item-price"
				>
					<strike
						class="products__item-price-old"
						v-if="originalPrice"
						v-html="$options.filters.fwgpricecent(originalPrice, 'short')"
					/>
					<span
						class="products__item-price-current"
						v-html="$options.filters.fwgpricecent(price, 'short')"
					/>
					<div
						v-if="co2Compensation"
						class="products__item-price-co2"
						v-html="$t('shoplist.product.co2Compensation', { amount: $options.filters.fwgpricecent(co2Compensation, 'short') })"
					/>
				</div>
				<div
					v-if="priceReduction"
					class="products__item-price-reduction"
					v-html="priceReduction"
				/>

				<template v-if="isShop && hasBasket && !menuMode && !disabled">
					<QuantityCounter
						v-if="!product.isEditable"
						:quantity="quantity"
						base-class="products__item-quantity"
						@sub="onSub()"
						@add="onAdd()"
					/>

					<div class="products__item-cart">
						<div
							class="products__item-cart-message"
							ref="message"
							:class="{'products__item-cart-message--added': addedToCart}"
							@click="onCloseMessage"
							v-html="$t('shoplist.product.successCart')"
						/>
						<!-- TODO: this message must be translated and dynamically filled -->
						<Button
							class="products__item-configure-button"
							@click.prevent="onConfig()"
							level="outline"
							type="button"
							:text="$t('shoplist.product.configButton')"
							:disabled="locked"
							small
							v-if="product.isConfigurable"
						/>
						<Button
							class="products__item-configure-button"
							@click.prevent="onCart()"
							level="outline"
							type="button"
							:text="$t('shoplist.product.cartButton')"
							:disabled="locked"
							small
							v-else
						/>
					</div>
				</template>
				<template v-else-if="isShop && !hasBasket && !menuMode && !disabled">
					<div class="products__item-cart">
						<Button
							class="products__item-public-button"
							@click.prevent="$emit('orderbox')"
							level="outline"
							type="button"
							:text="$t('shoplist.product.startOrder')"
							small
						/>
					</div>
				</template>
				<div v-else-if="!isShop && !menuMode && !disabled">
					<Button
						class="product-overlay__shop-button"
						@click.prevent="$emit('shop')"
						level="outline"
							type="button"
							:text="$t('shoplist.product.shopButton')"
							small
					/>
				</div>

			</div>
			<div class="product-overlay__image">
				<figure class="product-overlay__image-wrapper">
					<img
						class="product-overlay__image-element"
						:src="product.image"
						loading="lazy"
					>
				</figure>
				<img
					class="product-overlay__image-label"
					v-if="productLabelText"
					:src="productLabelText"
					loading="lazy"
				>
			</div>
		</div>
		<div class="product-overlay__tabs">
			<button
				class="product-overlay__tab"
				:class="{
					'product-overlay__tab--active': activeTab == 'nutrition',
				}"
				type="button"
				v-html="$t('shoplist.product.details.nutrition')"
				@click="changeActiveTab('nutrition')"
			/>
			<button
				class="product-overlay__tab"
				:class="{
					'product-overlay__tab--active': activeTab == 'ratings',
				}"
				type="button"
				v-html="$t('shoplist.product.details.ratings.tab')"
				@click="changeActiveTab('ratings')"
			/>
		</div>
		<div
			class="product-overlay__tab-content"
			v-if="activeTab == 'nutrition'"
		>
			<div
				class="product-overlay__details"
				v-if="hasFeature('FEATURE_NUTRITION_DETAILS')"
			>
				<div class="product-overlay__details-wrapper">
					<div
						class="product-overlay__details-block"
						v-if="variantNutrients"
					>
						<h3
							class="product-overlay__details-title"
							v-html="$t('shoplist.product.details.nutrition')"
						/>
						<table class="product-overlay__details-table">
							<tr
								class="product-overlay__details-table-row"
								v-for="nutrient in variantNutrients"
								:key="nutrient.key"
							>
								<th
									class="product-overlay__details-table-cell"
									v-html="nutrient.label"
								/>
								<td
									class="product-overlay__details-table-cell"
									v-html="nutrient.value"
								/>
							</tr>
						</table>
					</div>
					<div
						class="product-overlay__details-block"
						v-if="variantDetails && variantDetails.co2"
					>
						<h3
							class="product-overlay__details-title"
							v-html="$t('shoplist.product.details.co2.title')"
						/>
						<table class="product-overlay__details-table">
							<tr
								class="product-overlay__details-table-row"
							>
								<th
									class="product-overlay__details-table-cell"
									v-html="$t('shoplist.product.details.co2.emissionLabel')"
								/>
								<td
									class="product-overlay__details-table-cell"
									v-html="$t('shoplist.product.details.co2.emissionValue', {value: variantDetails.co2.emission})"
								/>
							</tr>
						</table>
						<span
							class="product-overlay__details-table-cell product-overlay__details-table-cell--small"
							v-html="$t('shoplist.product.details.co2.percentValue', {value: variantDetails.co2.percent})"
						/>
					</div>
				</div>

				<div class="product-overlay__details-wrapper">
					<div
						class="product-overlay__details-block"
						v-if="variantAllergens"
					>
						<h3
							class="product-overlay__details-title"
							v-html="$t('shoplist.product.details.allergens.title')"
						/>
						<ul class="product-overlay__details-allergens">
							<li
								class="product-overlay__details-allergen"
								v-for="allergen in variantAllergens"
								:key="allergen.key"
							>
								<span class="product-overlay__details-allergen-icon">
									<component
										:is="allergen.icon"
									/>
								</span>
								<span
									class="product-overlay__details-allergen-label"
									v-html="allergen.label"
								/>
							</li>
						</ul>
					</div>
					<div
						class="product-overlay__details-block"
						v-if="variantTastes"
					>
						<h3
							class="product-overlay__details-title"
							v-html="$t('shoplist.product.details.taste.title')"
						/>
						<ul class="product-overlay__details-tastes">
							<li
								class="product-overlay__details-taste"
								v-for="taste in variantTastes"
								:key="taste.key"
								v-html="taste.label"
							>
							</li>
						</ul>
					</div>
				</div>
			</div>
			<div
				class="product-overlay__details"
				v-else
			>
				<div class="product-overlay__details-wrapper">
					<div class="product-overlay__details-block">
						<h3
							class="product-overlay__details-title"
							v-html="$t('shoplist.product.details.allergens.title')"
						/>
						<div
							class="product-overlay__info-text"
							v-html="$options.filters.fwgcmstext($te('shoplist.product.tempNutrition') ? $t('shoplist.product.tempNutrition') : product.nutritionInfos || '-')"
						/>
					</div>
				</div>
			</div>
		</div>
		<div
			class="product-overlay__tab-content"
			v-if="activeTab == 'ratings'"
		>
			<div class="product-overlay__details product-overlay__details--reviews">
				<div class="product-overlay__details-block">
					<h3 class="product-overlay__details-title">
						<span v-html="$t('shoplist.product.details.ratings.title')"/>
            <span v-html="`&nbsp;(${ratingsCount})`"/>
						<ProductRating
							class="product-overlay__details-title-rating"
							:rating="product.averageRatings"
						/>
					</h3>
					<ul
						class="product-overlay__reviews"
						v-if="currentProductReviews.reviews && currentProductReviews.reviews.length"
					>
						<li
							class="product-overlay__review"
							v-for="review in currentProductReviews.reviews"
							:key="`product-review-${review.id}`"
						>
							<div class="product-overlay__review-name">
								<strong v-sanitize="review.date"/>
								<span v-sanitize="review.title"/>
							</div>
              <ProductRating
                class="product-overlay__review-name-rating"
                :rating="review.rating"
              />
							<div
								class="product-overlay__review-comment"
								v-sanitize="review.comment"
							/>
						</li>
					</ul>
					<div
						class="product-overlay__no-reviews"
						v-else
						v-html="$t('shoplist.product.details.ratings.empty')"
					/>
				</div>
				<div
					class="product-overlay__details-block"
					v-if="!menuMode"
				>
					<h3
						class="product-overlay__details-title"
						v-html="$t('shoplist.product.details.ratings.formTitle')"
					/>
					<ProductReviewForm
						:code="code"
					/>
				</div>
			</div>

		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import MixinProductLabels from "@/mixins/MixinProductLabels";
import ProductRating from '@/components/ProductRating';
import Button from '@/components/Button';
import QuantityCounter from '@/components/QuantityCounter';
import ProductReviewForm from '@/components/ProductReviewForm';

export default {
	mixins: [
		MixinProductLabels,
	],
	components: {
		ProductRating,
		Button,
		QuantityCounter,
		ProductReviewForm,
	},
	props: {
		code: [String, Number],
		productData: Object,
		isShop: Boolean,
		locked: Boolean,
		disabled: Boolean,
	},
	data() {
		return {
			details: null,
			activeTab: 'nutrition',
			selectedVariant: 0,
			quantity: 1,
			addedToCart: false,
		};
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"accountUserDetail",
		]),
		...mapGetters('productlist', [
			'currentProduct',
			'currentProductDetails',
			'currentProductReviews',
			'menuMode',
		]),
		...mapGetters('cart', [
			'hasBasket',
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		product() {
			return this.currentProductDetails || this.productData;
		},
		price() {
			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				return this.product.variants[this.selectedVariant].price;
			}
			return this.product.price;
		},
		originalPrice() {
			let originalPrice = this.product.originalPrice;

			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				originalPrice = this.product.variants[this.selectedVariant].originalPrice;
			}
			return !!originalPrice && this.price < originalPrice && originalPrice;
		},
		co2Compensation() {
			return this.hasFeature('FEATURE_PRODUCT_CO2_REMOVAL') && this.variant?.co2?.amount
		},
		priceReduction() {
			if (this.originalPrice) {
				if (this.product.discountInPercent) {
					return `-${Math.floor(100 / this.originalPrice * (this.originalPrice - this.price))}%`;
				}
				else {
					return `-${this.$options.filters.fwgpricecent((this.originalPrice - this.price), 'short')} CHF`;
				}
			}
			return false;
		},
		variant() {
			if (this.product?.variants.length) {
				if (this.product.variants.length > this.selectedVariant) {
					return this.product.variants[this.selectedVariant];
				}
				return this.product.variants[0];
			}
			else {
				return false;
			}
		},
		availableVariants() {
			// only show variants that have stock
			return this.product?.variants.filter(v => v.stock != 0) || []
		},
		variantDetails() {
			return this.product?.variants.find(v => v.code == this.variant.code)
		},

		variantNutrients() {
			const variant = this.variantDetails;

			if (variant) {
				const nutrients = variant.nutrient;

				if (nutrients) {
					return Object.keys(nutrients).map(key => ({
						key,
						label: this.$t(`shoplist.product.details.${key}Label`),
						value: this.$t(`shoplist.product.details.${key}Value`, {value: nutrients[key]}),
					}));
				}
			}

			return false;
		},
		variantAllergens() {
			const variant = this.variantDetails;

			if (variant) {
				const allergens = variant.allergen;
				// const allergens = [ 'celery', 'crab', 'egg', 'fish', 'gluten', 'lactose', 'lupin', 'mollusc', 'mustard', 'nut', 'peanut', 'sesame', 'soja', 'sulfur', ];

				if (allergens) {
					return allergens.map(key => ({
						key,
						icon: this.iconComponent(`allergens-${key}`),
						label: this.$t(`shoplist.product.details.allergens.${key}`),
					}));
				}
			}

			return false;
		},
		variantTastes() {
			const variant = this.variantDetails;

			if (variant) {
				const tastes = variant.taste;

				if (tastes) {
					return tastes.map(key => ({
						key,
						label: this.$te(`shoplist.product.details.taste.${key}`) ? this.$t(`shoplist.product.details.taste.${key}`) : key,
					}));
				}
			}

			return false;
		},
		stock() {
			if (this.product.variants.length && this.product.variants.length > this.selectedVariant) {
				if (!isNaN(this.product.variants[this.selectedVariant].stock)) {
					return this.product.variants[this.selectedVariant].stock;
				}
			}
			return this.product.stock;
		},
    ratingsCount() {
      return this.currentProductReviews?.reviews?.length || 0
    }
	},
	watch: {
		locked: function (value) {
			if (value) {
				this.onAddedToCart();
			}
		}
	},
	methods: {
		onSub() {
			this.quantity = Math.max(this.quantity - 1, 1);
			this.$emit('quantitychange', this.quantity);
		},
		onAdd() {
			this.quantity = this.quantity + 1;
			this.$emit('quantitychange', this.quantity);
		},
		onConfig() {
			const item = {
				...this.product,
				price: this.price,
				variant: this.variant.name,
				selectedVariant: this.variant.code,
			}

			this.$emit('config', item);
		},
		onCart() {
			this.addedToCart = false;

			const item = {
				quantity: this.quantity,
				productCode: this.product.code,
				variantCode: this.variant.code
			}
			if (this.product.isEditable) {
				item.forceCopy = true
			}

			this.$emit('addToCart', item);

			this.$nextTick(() => {
				this.quantity = 1;
			});
		},
		onAddedToCart() {
			this.addedToCart = true;

			if (this.$refs.message.getBoundingClientRect().top < 0) {
				scrrroll.to(this.$refs.message, 300);
			}

			clearTimeout(this.messageTimeout);
			this.messageTimeout = setTimeout(() => {
				this.addedToCart = false;
			}, 6000);
		},
		onCloseMessage(e) {
			clearTimeout(this.messageTimeout);
			this.addedToCart = false;
		},


		changeActiveTab(tab) {
			this.activeTab = tab;
		},
	},
	async created() {
		this.loading = true;
		await Promise.all([
			this.$store.dispatch('productlist/getProductDetails', { code: this.code, accumulatorGroup: 'productDetails' }),
			this.$store.dispatch('productlist/getProductReviews', { code: this.code, accumulatorGroup: 'productDetails' }),
		]);
		this.loading = false;
	},
}
</script>

<style>

</style>
